import type { Uuid } from '../types/api';
import type { LiniyaHttpClient } from '../http/client';
import type {
  Message,
  MessageHistoryPage,
  MessageReaction,
  SendMessageAck,
  SendMessageRequest,
} from '../types/message';

export interface HistoryQuery {
  limit?: number;
  /** Load the page immediately older than this sequence. */
  beforeSequence?: number;
  signal?: AbortSignal;
}

export class MessagesApi {
  constructor(private readonly http: LiniyaHttpClient) {}

  /**
   * Load one page of history, newest last.
   *
   * History is paged from the bottom upwards: a chat with years of messages
   * must not be downloaded in full just to show the last screen of it.
   */
  async history(chatId: Uuid, query: HistoryQuery = {}): Promise<MessageHistoryPage> {
    return await this.http.request<MessageHistoryPage>(`/api/v1/chats/${chatId}/messages/`, {
      query: {
        limit: query.limit,
        before_sequence: query.beforeSequence,
      },
      ...(query.signal ? { signal: query.signal } : {}),
    });
  }

  /**
   * Send over HTTP.
   *
   * This is the fallback path; the WebSocket carries the same command with the
   * same payload when a connection is up. Both are idempotent on
   * `message_id`, so a message that goes out on both paths after a flaky
   * reconnect still lands exactly once.
   */
  async send(request: SendMessageRequest): Promise<{ message: Message; ack: SendMessageAck }> {
    return await this.http.request<{ message: Message; ack: SendMessageAck }>(
      `/api/v1/chats/${request.chat_id}/messages/`,
      { method: 'POST', body: request },
    );
  }

  async edit(messageId: Uuid, content: string): Promise<Message> {
    const response = await this.http.request<{ message: Message }>(
      `/api/v1/messages/${messageId}/`,
      { method: 'PATCH', body: { content } },
    );
    return response.message;
  }

  async remove(messageId: Uuid): Promise<void> {
    await this.http.request<void>(`/api/v1/messages/${messageId}/`, { method: 'DELETE' });
  }

  /** Toggle the caller's reaction; sending the same emoji again removes it. */
  async react(messageId: Uuid, emoji: string): Promise<MessageReaction[]> {
    const response = await this.http.request<{ reactions: MessageReaction[] }>(
      `/api/v1/messages/${messageId}/reaction/`,
      { method: 'POST', body: { emoji } },
    );
    return response.reactions;
  }

  async report(messageId: Uuid): Promise<void> {
    await this.http.request<void>(`/api/v1/messages/${messageId}/report/`, {
      method: 'POST',
      body: {},
    });
  }
}
